import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./Sidebar";
import "./ExamRecordsScreen.css";
import { useNavigate, useLocation } from "react-router-dom";

const ExamRecords = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const classNameFromState = location.state?.className || "";

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClass, setSelectedClass] = useState(classNameFromState);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedExamType, setSelectedExamType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [examRecords, setExamRecords] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [sections, setSections] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedExam, setSelectedExam] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailedRecords, setDetailedRecords] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [examTypes] = useState([
    "1st Assessment",
    "2nd Assessment",
    "3rd Assessment",
    "Finals",
    "Mid-Term",
    "Pre-Board",
    "Mock Test",
    "Unit Test",
    "Quiz",
  ]);

  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  // Fetch exam records from backend
  const fetchExamRecords = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        action: "get-exam-records",
        page: page,
        limit: 50, // Increased limit to get more records
      });

      if (searchQuery) params.append("exam_id", searchQuery);
      if (selectedClass) params.append("class", selectedClass);
      if (selectedSubject) params.append("subject", selectedSubject);
      if (selectedSection) params.append("section", selectedSection);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedExamType) params.append("exam_type", selectedExamType);

      const response = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageExamSheets.php?${params}`
      );
      const data = await response.json();

      if (data.success) {
        // Group records by exam_id and other unique fields
        const groupedRecords = groupRecordsByUniqueExam(data.data);

        // Sort by exam_date (newest first)
        groupedRecords.sort((a, b) => {
          const dateA = new Date(a.exam_date);
          const dateB = new Date(b.exam_date);
          return dateB - dateA; // Newest first
        });

        setExamRecords(groupedRecords);
        setFilteredExams(groupedRecords);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalRecords(data.pagination?.total || 0);
      } else {
        setError(data.error || "Failed to fetch exam records");
        setExamRecords([]);
        setFilteredExams([]);
      }
    } catch (err) {
      setError("Network error: " + err.message);
      setExamRecords([]);
      setFilteredExams([]);
    } finally {
      setLoading(false);
    }
  }, [
    searchQuery,
    selectedClass,
    selectedSubject,
    selectedSection,
    dateFrom,
    dateTo,
    page,
    selectedExamType,
  ]);

  // Fetch detailed exam data
  const fetchExamDetails = async (examId) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageExamSheets.php?action=get-exam-by-id&exam_id=${examId}`
      );
      const data = await response.json();

      if (data.success) {
        setDetailedRecords(data.data);
        return data.data;
      } else {
        console.error("Failed to fetch exam details:", data.error);
        return [];
      }
    } catch (err) {
      console.error("Failed to fetch exam details:", err);
      return [];
    } finally {
      setLoadingDetails(false);
    }
  };

  // Group records by unique combination of exam fields
  const groupRecordsByUniqueExam = (records) => {
    if (!records || records.length === 0) return [];

    const grouped = {};

    records.forEach((record) => {
      // Create a unique key for each exam
      const uniqueKey = `${record.exam_id}_${record.exam_type}_${record.subject}_${record.class}_${record.class_section}_${record.exam_date}`;

      if (!grouped[uniqueKey]) {
        grouped[uniqueKey] = {
          id: uniqueKey,
          exam_id: record.exam_id,
          exam_type: record.exam_type,
          subject: record.subject,
          class: record.class,
          class_section: record.class_section,
          exam_date: record.exam_date,
          total_marks: parseFloat(record.total_marks),
          student_count: 1,
          avg_percentage: parseFloat(record.percentage) || 0,
          max_marks: parseFloat(record.obtained_marks) || 0,
          min_marks: parseFloat(record.obtained_marks) || 0,
          total_obtained: parseFloat(record.obtained_marks) || 0,
        };
      } else {
        grouped[uniqueKey].student_count += 1;
        grouped[uniqueKey].avg_percentage += parseFloat(record.percentage) || 0;
        grouped[uniqueKey].total_obtained +=
          parseFloat(record.obtained_marks) || 0;
        grouped[uniqueKey].max_marks = Math.max(
          grouped[uniqueKey].max_marks,
          parseFloat(record.obtained_marks) || 0
        );
        grouped[uniqueKey].min_marks = Math.min(
          grouped[uniqueKey].min_marks,
          parseFloat(record.obtained_marks) || 0
        );
      }
    });

    // Calculate average percentage for each exam
    Object.values(grouped).forEach((exam) => {
      exam.avg_percentage = (exam.avg_percentage / exam.student_count).toFixed(
        2
      );
      exam.avg_marks = (exam.total_obtained / exam.student_count).toFixed(2);
    });

    return Object.values(grouped);
  };

  // Fetch subjects when class is selected
  const fetchSubjects = async (className) => {
    if (!className) {
      setSubjects([]);
      return;
    }

    try {
      const response = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageExamSheets.php?action=get-subjects&className=${encodeURIComponent(
          className
        )}`
      );
      const data = await response.json();

      if (data.success) {
        setSubjects(data.data || []);
      } else {
        setSubjects([]);
      }
    } catch (err) {
      console.error("Failed to fetch subjects:", err);
      setSubjects([]);
    }
  };

  // Fetch sections when class is selected
  const fetchSections = async (className) => {
    if (!className) {
      setSections([]);
      return;
    }

    try {
      const response = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageExamSheets.php?action=get-class-sections&className=${encodeURIComponent(
          className
        )}`
      );
      const data = await response.json();

      if (data.success) {
        setSections(data.data || []);
      } else {
        setSections([]);
      }
    } catch (err) {
      console.error("Failed to fetch sections:", err);
      setSections([]);
    }
  };

  useEffect(() => {
    fetchExamRecords();
  }, [fetchExamRecords]);

  useEffect(() => {
    if (selectedClass) {
      fetchSubjects(selectedClass);
      fetchSections(selectedClass);
    } else {
      setSubjects([]);
      setSections([]);
    }
  }, [selectedClass]);

  // Local search filter
  useEffect(() => {
    if (!searchQuery) {
      setFilteredExams(examRecords);
      return;
    }

    const filtered = examRecords.filter(
      (exam) =>
        (exam.exam_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          exam.exam_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          exam.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          exam.class?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          exam.class_section
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase())) ??
        false
    );

    setFilteredExams(filtered);
  }, [searchQuery, examRecords]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchExamRecords();
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedClass(classNameFromState || "");
    setSelectedSubject("");
    setSelectedSection("");
    setSelectedExamType("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const handleViewDetails = async (exam) => {
    setSelectedExam(exam);
    const details = await fetchExamDetails(exam.exam_id);
    setDetailedRecords(details);
    setIsModalOpen(true);
  };

  const handleViewExam = (examId) => {
    navigate(`/view-exam-records/${examId}`); // Changed route
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedExam(null);
    setDetailedRecords([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getPerformanceColor = (percentage) => {
    const perc = parseFloat(percentage) || 0;
    if (perc >= 80) return "#00b09b"; // Excellent - Green
    if (perc >= 60) return "#6a11cb"; // Good - Purple
    if (perc >= 40) return "#f7971e"; // Average - Orange
    return "#ff416c"; // Needs Improvement - Red
  };

  const getPerformanceLabel = (percentage) => {
    const perc = parseFloat(percentage) || 0;
    if (perc >= 80) return "Excellent";
    if (perc >= 60) return "Good";
    if (perc >= 40) return "Average";
    return "Needs Improvement";
  };

  const getCardColorClass = (index) => {
    const colors = [
      "pastel-blue",
      "pastel-lavender",
      "pastel-mint",
      "pastel-coral",
      "pastel-peach",
      "pastel-aqua",
      "pastel-lilac",
      "pastel-sunshine",
      "pastel-rose",
      "pastel-sky",
      "pastel-lime",
      "pastel-sand",
    ];
    return colors[index % colors.length];
  };

  // Calculate unique classes count
  const getUniqueClassesCount = () => {
    if (!examRecords || examRecords.length === 0) return 0;
    if (selectedClass) return 1;
    const uniqueClasses = [...new Set(examRecords.map((exam) => exam.class))];
    return uniqueClasses.length;
  };

  return (
    <div className="exam-records-screen">
      <div className="exam-records-dashboard-wrapper">
        <Sidebar />
        <main className="exam-records-main-content">
          {/* Header Section */}
          <div className="exam-records-content-header">
            <div className="exam-records-header-left">
              <h1 className="exam-records-main-title">Exam Records</h1>
              <p className="exam-records-subtitle">
                View and manage all exam records with detailed insights
              </p>
            </div>
            <div className="exam-records-header-actions">
              <button
                className="exam-records-action-btn exam-records-secondary-btn"
                onClick={handleResetFilters}
              >
                <span className="exam-records-btn-icon">🔄</span>
                <span className="exam-records-btn-text">Reset Filters</span>
              </button>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="exam-records-stats-overview">
            <div className="exam-records-stat-card exam-records-stat-gradient-purple">
              <div className="exam-records-stat-icon">📊</div>
              <div className="exam-records-stat-content">
                <div className="exam-records-stat-value">
                  {filteredExams.length}
                </div>
                <div className="exam-records-stat-label">Total Exams</div>
              </div>
            </div>
            <div className="exam-records-stat-card exam-records-stat-gradient-blue">
              <div className="exam-records-stat-icon">🏫</div>
              <div className="exam-records-stat-content">
                <div className="exam-records-stat-value">
                  {getUniqueClassesCount()}
                </div>
                <div className="exam-records-stat-label">Classes</div>
              </div>
            </div>
            <div className="exam-records-stat-card exam-records-stat-gradient-green">
              <div className="exam-records-stat-icon">📚</div>
              <div className="exam-records-stat-content">
                <div className="exam-records-stat-value">{subjects.length}</div>
                <div className="exam-records-stat-label">Subjects</div>
              </div>
            </div>
            <div className="exam-records-stat-card exam-records-stat-gradient-orange">
              <div className="exam-records-stat-icon">👥</div>
              <div className="exam-records-stat-content">
                <div className="exam-records-stat-value">{sections.length}</div>
                <div className="exam-records-stat-label">Sections</div>
              </div>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="exam-records-search-filter-section">
            <form onSubmit={handleSearch} className="exam-records-filter-form">
              <div className="exam-records-search-container">
                <div className="exam-records-search-wrapper">
                  <div className="exam-records-search-icon-wrapper">
                    <svg
                      className="exam-records-search-icon"
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                    >
                      <path
                        fill="currentColor"
                        d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="exam-records-search-input"
                    placeholder="Search by Exam ID, Type, Subject, Class, or Section..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="exam-records-clear-search"
                      onClick={() => setSearchQuery("")}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              <div className="exam-records-filter-grid">
                <div className="exam-records-filter-group">
                  <label className="exam-records-filter-label">Class</label>
                  <select
                    className="exam-records-filter-select"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    <option value="">All Classes</option>
                    {classes.map((cls, index) => (
                      <option key={index} value={cls}>
                        {cls}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="exam-records-filter-group">
                  <label className="exam-records-filter-label">Subject</label>
                  <select
                    className="exam-records-filter-select"
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    disabled={!selectedClass}
                  >
                    <option value="">All Subjects</option>
                    {subjects.map((subject, index) => (
                      <option key={index} value={subject.name}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="exam-records-filter-group">
                  <label className="exam-records-filter-label">Section</label>
                  <select
                    className="exam-records-filter-select"
                    value={selectedSection}
                    onChange={(e) => setSelectedSection(e.target.value)}
                    disabled={!selectedClass}
                  >
                    <option value="">All Sections</option>
                    {sections.map((section, index) => (
                      <option key={index} value={section.section_hint}>
                        {section.section_hint}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="exam-records-filter-group">
                  <label className="exam-records-filter-label">Exam Type</label>
                  <select
                    className="exam-records-filter-select"
                    value={selectedExamType}
                    onChange={(e) => setSelectedExamType(e.target.value)}
                  >
                    <option value="">All Exam Types</option>
                    {examTypes.map((type, index) => (
                      <option key={index} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="exam-records-filter-group">
                  <label className="exam-records-filter-label">Date From</label>
                  <input
                    type="date"
                    className="exam-records-filter-date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>

                <div className="exam-records-filter-group">
                  <label className="exam-records-filter-label">Date To</label>
                  <input
                    type="date"
                    className="exam-records-filter-date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>

                <div className="exam-records-filter-group exam-records-filter-actions">
                  <button
                    type="submit"
                    className="exam-records-filter-btn exam-records-filter-apply"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="exam-records-loading">
              <div className="exam-records-loading-spinner"></div>
              <p>Loading exam records...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="exam-records-error">
              <div className="exam-records-error-icon">⚠️</div>
              <p>{error}</p>
              <button
                onClick={fetchExamRecords}
                className="exam-records-error-retry"
              >
                Retry
              </button>
            </div>
          )}

          {/* Exam Records Grid */}
          {!loading && !error && (
            <>
              <div className="exam-records-grid-header">
                <h2 className="exam-records-section-title">
                  Exam Records ({filteredExams.length})
                </h2>
                <div className="exam-records-view-options">
                  <span className="exam-records-view-label">Showing:</span>
                  <span className="exam-records-view-count">
                    {filteredExams.length} exams
                  </span>
                </div>
              </div>

              {filteredExams.length === 0 ? (
                <div className="exam-records-empty">
                  <div className="exam-records-empty-icon">📊</div>
                  <h3>No Exam Records Found</h3>
                  <p>Try adjusting your filters or create a new exam.</p>
                  <button
                    className="exam-records-empty-btn"
                    onClick={() => navigate("/add-exam")}
                  >
                    Create New Exam
                  </button>
                </div>
              ) : (
                <>
                  <div className="exam-records-grid">
                    {filteredExams.map((exam, index) => {
                      const performanceColor = getPerformanceColor(
                        exam.avg_percentage
                      );
                      const performanceLabel = getPerformanceLabel(
                        exam.avg_percentage
                      );

                      return (
                        <div
                          className={`exam-records-card exam-records-card-${getCardColorClass(
                            index
                          )}`}
                          key={exam.id || exam.exam_id}
                          data-status={performanceLabel.toLowerCase()}
                        >
                          <div className="exam-records-card-header">
                            <div className="exam-records-exam-info">
                              <h3 className="exam-records-exam-name">
                                {exam.exam_type || `Exam #${exam.exam_id}`}
                              </h3>
                              <div className="exam-records-exam-meta">
                                <div className="exam-records-meta-item">
                                  <span className="exam-records-meta-icon">
                                    #
                                  </span>
                                  <span className="exam-records-meta-value">
                                    {exam.exam_id}
                                  </span>
                                </div>
                                <div className="exam-records-meta-item">
                                  <span className="exam-records-meta-icon">
                                    📅
                                  </span>
                                  <span className="exam-records-meta-value">
                                    {formatDate(exam.exam_date)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div
                              className="exam-records-performance-badge"
                              style={{ backgroundColor: performanceColor }}
                            >
                              {performanceLabel}
                            </div>
                          </div>

                          <div className="exam-records-card-body">
                            <div className="exam-records-details-grid">
                              <div className="exam-records-detail-item">
                                <span className="exam-records-detail-label">
                                  Exam Type
                                </span>
                                <span className="exam-records-detail-value">
                                  {exam.exam_type || "N/A"}
                                </span>
                              </div>
                              <div className="exam-records-detail-item">
                                <span className="exam-records-detail-label">
                                  Subject
                                </span>
                                <span className="exam-records-detail-value">
                                  {exam.subject || "N/A"}
                                </span>
                              </div>
                              <div className="exam-records-detail-item">
                                <span className="exam-records-detail-label">
                                  Class
                                </span>
                                <span className="exam-records-detail-value">
                                  {exam.class || "N/A"}
                                </span>
                              </div>
                              <div className="exam-records-detail-item">
                                <span className="exam-records-detail-label">
                                  Section
                                </span>
                                <span className="exam-records-detail-value">
                                  {exam.class_section || "N/A"}
                                </span>
                              </div>
                              <div className="exam-records-detail-item">
                                <span className="exam-records-detail-label">
                                  Total Marks
                                </span>
                                <span className="exam-records-detail-value">
                                  {exam.total_marks || 0}
                                </span>
                              </div>
                              <div className="exam-records-detail-item">
                                <span className="exam-records-detail-label">
                                  Students
                                </span>
                                <span className="exam-records-detail-value">
                                  {exam.student_count || 0}
                                </span>
                              </div>
                              <div className="exam-records-detail-item">
                                <span className="exam-records-detail-label">
                                  Avg Score
                                </span>
                                <span className="exam-records-detail-value">
                                  {(exam.avg_percentage || 0) + "%"}
                                </span>
                              </div>
                            </div>

                            <div className="exam-records-progress-section">
                              <div className="exam-records-progress-header">
                                <span className="exam-records-progress-label">
                                  Performance Range
                                </span>
                                <span className="exam-records-progress-stats">
                                  Max: {exam.max_marks || 0} | Min:{" "}
                                  {exam.min_marks || 0}
                                </span>
                              </div>
                              <div className="exam-records-progress-bar">
                                <div
                                  className="exam-records-progress-fill"
                                  style={{
                                    width: `${exam.avg_percentage || 0}%`,
                                    background: `linear-gradient(90deg, ${performanceColor}, ${performanceColor}80)`,
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>

                          <div className="exam-records-card-footer">
                            <button
                              className="exam-records-footer-btn exam-records-footer-details-btn"
                              onClick={() => handleViewDetails(exam)}
                            >
                              <span className="exam-records-footer-btn-icon">
                                📋
                              </span>
                              <span className="exam-records-footer-btn-text">
                                Details
                              </span>
                            </button>
                            <button
                              className="exam-records-footer-btn exam-records-footer-view-btn"
                              onClick={() => handleViewExam(exam.exam_id)}
                            >
                              <span className="exam-records-footer-btn-icon">
                                👁️
                              </span>
                              <span className="exam-records-footer-btn-text">
                                View Exam
                              </span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="exam-records-pagination">
                      <button
                        className="exam-records-pagination-btn"
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                        disabled={page === 1}
                      >
                        Previous
                      </button>

                      <div className="exam-records-pagination-pages">
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (page <= 3) {
                              pageNum = i + 1;
                            } else if (page >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = page - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                className={`exam-records-pagination-page ${
                                  page === pageNum ? "active" : ""
                                }`}
                                onClick={() => setPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                        )}
                      </div>

                      <button
                        className="exam-records-pagination-btn"
                        onClick={() =>
                          setPage((prev) => Math.min(totalPages, prev + 1))
                        }
                        disabled={page === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Details Modal */}
          {isModalOpen && selectedExam && (
            <div className="exam-records-modal-overlay">
              <div className="exam-records-modal">
                <div className="exam-records-modal-header">
                  <h2 className="exam-records-modal-title">
                    {selectedExam.exam_type || `Exam #${selectedExam.exam_id}`}{" "}
                    Details
                  </h2>
                  <button
                    className="exam-records-modal-close"
                    onClick={closeModal}
                  >
                    ✕
                  </button>
                </div>

                <div className="exam-records-modal-body">
                  {/* Exam Summary */}
                  <div className="exam-records-modal-summary">
                    <div className="exam-records-summary-grid">
                      <div className="exam-records-summary-item">
                        <span className="exam-records-summary-label">
                          Exam ID
                        </span>
                        <span className="exam-records-summary-value">
                          {selectedExam.exam_id}
                        </span>
                      </div>
                      <div className="exam-records-summary-item">
                        <span className="exam-records-summary-label">
                          Exam Type
                        </span>
                        <span className="exam-records-summary-value">
                          {selectedExam.exam_type || "N/A"}
                        </span>
                      </div>
                      <div className="exam-records-summary-item">
                        <span className="exam-records-summary-label">
                          Subject
                        </span>
                        <span className="exam-records-summary-value">
                          {selectedExam.subject || "N/A"}
                        </span>
                      </div>
                      <div className="exam-records-summary-item">
                        <span className="exam-records-summary-label">
                          Class
                        </span>
                        <span className="exam-records-summary-value">
                          {selectedExam.class || "N/A"}
                        </span>
                      </div>
                      <div className="exam-records-summary-item">
                        <span className="exam-records-summary-label">
                          Section
                        </span>
                        <span className="exam-records-summary-value">
                          {selectedExam.class_section || "N/A"}
                        </span>
                      </div>
                      <div className="exam-records-summary-item">
                        <span className="exam-records-summary-label">Date</span>
                        <span className="exam-records-summary-value">
                          {formatDate(selectedExam.exam_date)}
                        </span>
                      </div>
                      <div className="exam-records-summary-item">
                        <span className="exam-records-summary-label">
                          Total Marks
                        </span>
                        <span className="exam-records-summary-value">
                          {selectedExam.total_marks || 0}
                        </span>
                      </div>
                      <div className="exam-records-summary-item">
                        <span className="exam-records-summary-label">
                          Total Students
                        </span>
                        <span className="exam-records-summary-value">
                          {selectedExam.student_count || 0}
                        </span>
                      </div>
                      <div className="exam-records-summary-item">
                        <span className="exam-records-summary-label">
                          Average Score
                        </span>
                        <span className="exam-records-summary-value">
                          {(selectedExam.avg_percentage || 0) + "%"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Students List */}
                  <div className="exam-records-modal-students">
                    <h3 className="exam-records-students-title">
                      Student Results ({detailedRecords.length})
                    </h3>

                    {loadingDetails ? (
                      <div className="exam-records-students-loading">
                        <div className="exam-records-students-spinner"></div>
                        <p>Loading student details...</p>
                      </div>
                    ) : detailedRecords.length > 0 ? (
                      <div className="exam-records-students-table-container">
                        <table className="exam-records-students-table">
                          <thead>
                            <tr>
                              <th>Student ID</th>
                              <th>Student Name</th>
                              <th>Obtained Marks</th>
                              <th>Percentage</th>
                              <th>Grade</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailedRecords.map((record, index) => {
                              const studentGrade = getPerformanceLabel(
                                record.percentage
                              );
                              const studentColor = getPerformanceColor(
                                record.percentage
                              );

                              return (
                                <tr key={index}>
                                  <td>{record.student_id || "N/A"}</td>
                                  <td>{record.student_name || "N/A"}</td>
                                  <td>
                                    <span className="exam-records-marks-display">
                                      {record.obtained_marks || 0}/
                                      {selectedExam.total_marks || 0}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="exam-records-percentage-display">
                                      {(record.percentage || 0) + "%"}
                                    </span>
                                  </td>
                                  <td>
                                    <span
                                      className="exam-records-grade-badge"
                                      style={{ backgroundColor: studentColor }}
                                    >
                                      {studentGrade}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="exam-records-students-empty">
                        <p>No student records found for this exam.</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="exam-records-modal-footer">
                  <button
                    className="exam-records-modal-btn exam-records-modal-close-btn"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                  <button
                    className="exam-records-modal-btn exam-records-modal-export-btn"
                    onClick={() => {
                      alert("Export functionality would be implemented here");
                    }}
                  >
                    Export Results
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ExamRecords;
